import type { AxiosResponse } from "axios";
import { reportError, reportMessage } from "@/utils/sentry";
import { api } from "@/api";

// 微信相关 TypeScript 类型定义
export interface WechatJsConfig {
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
}

export interface WechatJsConfigRequest {
  url: string;
}

export interface WechatJsConfigResponse {
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
}

export interface WechatLoginRequest {
  code: string;
}

export interface WechatLoginResponse {
  openid?: string;
  username?: string;
  session_key?: string;
  access_token?: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
  nickname?: string;
  headimgurl?: string;
  jwt_access_token?: string;
  jwt_refresh_token?: string;
  message?: string;
}

export interface WechatBindRequest {
  openid: string;
  userid: string;
}

export interface WechatBindResponse {
  success: boolean;
  message: string;
  access: string;
  refresh: string;
}

export interface WechatUpdateBindRequest {
  userid: string;
}

export interface WechatUpdateBindResponse {
  success: boolean;
  message: string;
  access: string;
  refresh: string;
}

export interface WechatUnbindResponse {
  success: boolean;
  message: string;
}

export interface WechatUserBinding {
  openid: string;
  userid: string;
  nickname: string;
  headimgurl: string;
  bindTime: string;
  lastUpdateTime: string;
}

export interface WechatUserOpenidResponse {
  message: string;
  openid?: string;
}

export interface WechatApiError {
  message: string;
  code?: string;
  details?: unknown;
}

/**
 * 微信 JS-SDK 配置接口
 * 获取微信 JS-SDK 初始化所需的配置信息
 *
 * @param url - 当前页面的完整 URL，微信服务器需要此 URL 来生成签名
 * @returns {Promise<WechatJsConfigResponse>} 微信 JS-SDK 配置信息
 *
 * @example
 * const config = await getWechatJsConfig('https://example.com/current-page');
 * wx.config(config);
 */
export async function getWechatJsConfig(
  url: string,
): Promise<WechatJsConfigResponse> {
  try {
    // 确保 URL 是完整的，包含协议
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      throw new Error("URL 必须包含协议（http:// 或 https://）");
    }

    // 移除 URL 中的 hash 部分，微信签名不包含 hash
    const cleanUrl = url.split("#")[0];

    const response: AxiosResponse<WechatJsConfigResponse> = await api.post(
      "wechat/js-config/",
      {
        url: cleanUrl,
      },
    );

    // 验证响应数据完整性
    const config = response.data;
    if (
      !config.appId ||
      !config.timestamp ||
      !config.nonceStr ||
      !config.signature
    ) {
      throw new Error("微信配置响应数据不完整");
    }

    reportMessage("获取微信JS-SDK配置成功", "info", {
      action: "get_wechat_js_config",
      url: cleanUrl,
      appId: config.appId,
      timestamp: new Date().toISOString(),
    });

    return config;
  } catch (error: unknown) {
    reportError(error as Error, {
      type: "wechat_api_error",
      action: "get_wechat_js_config",
      url,
      timestamp: new Date().toISOString(),
    });

    // 重新抛出错误，保持错误链
    throw error;
  }
}

/**
 * 微信授权登录接口
 * 通过微信 code 获取用户的微信信息和访问令牌
 *
 * @param code - 微信授权后获得的 code
 * @returns {Promise<WechatLoginResponse>} 包含用户信息和访问令牌的响应
 * 成功时包含: openid, username, session_key, access_token, expires_in, refresh_token, scope, nickname, headimgurl, jwt_access_token, jwt_refresh_token
 * 失败时包含: message
 *
 * @example
 * const result = await wechatLogin('wx_auth_code_here');
 * if (result.openid) {
 *   console.log('OpenID:', result.openid);
 *   console.log('用户名:', result.username);
 *   console.log('昵称:', result.nickname);
 *   console.log('JWT访问令牌:', result.jwt_access_token);
 *   console.log('JWT刷新令牌:', result.jwt_refresh_token);
 * } else {
 *   console.log('登录失败:', result.message);
 * }
 */
export async function wechatLogin(code: string): Promise<WechatLoginResponse> {
  try {
    if (!code || code.trim() === "") {
      throw new Error("微信授权 code 不能为空");
    }

    const response: AxiosResponse<WechatLoginResponse> = await api.post(
      "wechat/login/",
      {
        code: code.trim(),
      },
    );

    const result = response.data;

    // 记录成功的微信登录
    if (result.openid) {
      reportMessage("微信登录成功", "info", {
        action: "wechat_login",
        openid: result.openid.substring(0, 8) + "***", // 部分隐藏 openid
        nickname: result.nickname || "unknown",
        scope: result.scope || "unknown",
        hasAccessToken: !!result.access_token,
        timestamp: new Date().toISOString(),
      });
    }

    return result;
  } catch (error: unknown) {
    reportError(error as Error, {
      type: "wechat_api_error",
      action: "wechat_login",
      code: code ? "provided" : "empty", // 不记录实际的 code 值
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
}

/**
 * 绑定微信 openid 到用户账号
 * 将用户的学号/工号与微信 openid 进行绑定
 *
 * @param openid - 微信用户的 openid
 * @param userid - 用户的学号/工号
 * @returns {Promise<WechatBindResponse>} 绑定结果，包含 JWT tokens
 *
 * @example
 * const result = await bindWechatOpenid('openid_here', '20233802086');
 * // 绑定成功后可以直接使用返回的 access 和 refresh token
 */
export async function bindWechatOpenid(
  openid: string,
  userid: string,
): Promise<WechatBindResponse> {
  try {
    if (!openid || !userid) {
      throw new Error("openid 和 userid 都不能为空");
    }

    const response: AxiosResponse<WechatBindResponse> = await api.post(
      "wechat/openid/",
      {
        openid: openid.trim(),
        userid: userid.trim(),
      },
    );

    const result = response.data;

    // 记录成功的绑定操作
    reportMessage("微信账号绑定成功", "info", {
      action: "bind_wechat_openid",
      userid,
      openid: openid.substring(0, 8) + "***", // 部分隐藏 openid
      timestamp: new Date().toISOString(),
    });

    return result;
  } catch (error: unknown) {
    reportError(error as Error, {
      type: "wechat_api_error",
      action: "bind_wechat_openid",
      userid,
      openid: openid ? openid.substring(0, 8) + "***" : "empty",
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
}

/**
 * 获取微信 openid 绑定信息
 * 获取与该 openid 绑定的用户信息
 *
 * @param openid - 微信用户的 openid
 * @returns {Promise<WechatUserBinding>} 绑定信息
 *
 * @example
 * const binding = await getWechatBinding('openid_here');
 * console.log(`绑定用户: ${binding.nickname}`);
 */
export async function getWechatBinding(
  openid: string,
): Promise<WechatUserBinding> {
  try {
    if (!openid) {
      throw new Error("openid 不能为空");
    }

    const response: AxiosResponse<WechatUserBinding> = await api.get(
      `wechat/openid/${encodeURIComponent(openid)}/`,
    );

    const result = response.data;

    reportMessage("获取微信绑定信息成功", "info", {
      action: "get_wechat_binding",
      openid: openid.substring(0, 8) + "***",
      userid: result.userid,
      timestamp: new Date().toISOString(),
    });

    return result;
  } catch (error: unknown) {
    reportError(error as Error, {
      type: "wechat_api_error",
      action: "get_wechat_binding",
      openid: openid ? openid.substring(0, 8) + "***" : "empty",
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
}

/**
 * 更新微信 openid 绑定的用户账号
 *
 * @param openid - 微信用户的 openid
 * @param userid - 新的用户学号/工号
 * @returns {Promise<WechatUpdateBindResponse>} 更新结果，包含新的 JWT tokens
 *
 * @example
 * try {
 *   const result = await updateWechatBinding('openid_here', '20233802098');
 *   // 更新成功
 * } catch (error) {
 *   console.log('更新失败:', error.message);
 * }
 */
export async function updateWechatBinding(
  openid: string,
  userid: string,
): Promise<WechatUpdateBindResponse> {
  try {
    if (!openid || !userid) {
      throw new Error("openid 和 userid 都不能为空");
    }

    const response: AxiosResponse<WechatUpdateBindResponse> = await api.put(
      `wechat/openid/${encodeURIComponent(openid)}/`,
      {
        userid: userid.trim(),
      },
    );

    const result = response.data;

    // 记录成功的更新操作
    reportMessage("微信账号绑定更新成功", "info", {
      action: "update_wechat_binding",
      openid: openid.substring(0, 8) + "***",
      newUserid: userid,
      timestamp: new Date().toISOString(),
    });

    return result;
  } catch (error: unknown) {
    reportError(error as Error, {
      type: "wechat_api_error",
      action: "update_wechat_binding",
      openid: openid ? openid.substring(0, 8) + "***" : "empty",
      newUserid: userid,
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
}

/**
 * 检查是否在微信环境中
 * 通过 User-Agent 检测当前是否在微信内置浏览器中
 *
 * @returns {boolean} 是否在微信环境
 */
export function isWechatEnvironment(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("micromessenger");
}

/**
 * 检查是否为微信开发工具环境
 * 在微信开发者工具中进行调试时使用
 *
 * @returns {boolean} 是否为微信开发工具环境
 */
export function isWechatDevtools(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("wechatdevtools");
}

/**
 * 获取当前页面的签名 URL
 * 获取用于微信 JS-SDK 签名的 URL（去除 hash 部分）
 *
 * @returns {string} 用于签名的 URL
 */
export function getSignatureUrl(): string {
  if (typeof window === "undefined") {
    return "";
  }

  // 获取完整 URL 并移除 hash 部分
  const url = window.location.href.split("#")[0];

  // 对于 SPA，可能需要特殊处理
  // 微信分享后可能在 URL 末尾添加参数，需要动态获取
  return url;
}

/**
 * 构建微信授权登录 URL
 * 用于跳转到微信授权页面
 *
 * @param appid - 微信公众号 appid
 * @param redirectUri - 授权后重定向的 URI
 * @param scope - 授权作用域，'snsapi_base' 或 'snsapi_userinfo'
 * @param state - 可选的状态参数，用于防止 CSRF 攻击
 * @returns {string} 微信授权 URL
 */
export function buildWechatAuthUrl(
  appid: string,
  redirectUri: string,
  scope: "snsapi_base" | "snsapi_userinfo" = "snsapi_base",
  state?: string,
): string {
  const baseUrl = "https://open.weixin.qq.com/connect/oauth2/authorize";
  const params = new URLSearchParams({
    appid,
    redirect_uri: redirectUri,
    response_type: "code",
    scope,
  });

  if (state) {
    params.append("state", state);
  }

  return `${baseUrl}?${params.toString()}#wechat_redirect`;
}

/**
 * 解绑微信 openid
 * 将 openid 对应的学号设为 null，并记录解绑时间
 * 用于用户主动解除微信与学号的绑定关系
 *
 * @param openid - 要解绑的微信用户 openid
 * @returns {Promise<WechatUnbindResponse>} 解绑结果
 *
 * @example
 * try {
 *   const result = await unbindWechatOpenid('openid_here');
 *   if (result.success) {
 *     console.log('解绑成功:', result.message);
 *   }
 * } catch (error) {
 *   console.error('解绑失败:', error);
 * }
 */
export async function unbindWechatOpenid(
  openid: string,
): Promise<WechatUnbindResponse> {
  try {
    if (!openid) {
      throw new Error("openid 不能为空");
    }

    const response: AxiosResponse<WechatUnbindResponse> = await api.delete(
      `wechat/openid/${encodeURIComponent(openid)}/`,
    );

    const result = response.data;

    // 记录成功的解绑操作
    reportMessage("微信账号解绑成功", "info", {
      action: "unbind_wechat_openid",
      openid: openid.substring(0, 8) + "***", // 部分隐藏 openid
      timestamp: new Date().toISOString(),
    });

    return result;
  } catch (error: unknown) {
    reportError(error as Error, {
      type: "wechat_api_error",
      action: "unbind_wechat_openid",
      openid: openid ? openid.substring(0, 8) + "***" : "empty",
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
}

/**
 * 根据用户ID获取微信 openid
 * 通过用户学号/工号查询绑定的微信 openid
 *
 * @param userId - 用户的学号/工号
 * @returns {Promise<WechatUserOpenidResponse>} 查询结果，包含 openid 或错误信息
 *
 * @example
 * try {
 *   const result = await getWechatOpenidByUserId('20233802086');
 *   if (result.openid) {
 *     console.log('该用户的微信 OpenID:', result.openid);
 *   } else {
 *     console.log('提示信息:', result.message);
 *   }
 * } catch (error) {
 *   console.error('查询失败:', error);
 * }
 */
export async function getWechatOpenidByUserId(
  userId: string,
): Promise<WechatUserOpenidResponse> {
  try {
    if (!userId) {
      throw new Error("用户ID不能为空");
    }

    const response: AxiosResponse<WechatUserOpenidResponse> = await api.get(
      `wechat/userid/${encodeURIComponent(userId.trim())}/`,
    );

    const result = response.data;

    // 记录查询操作
    reportMessage("查询用户微信绑定信息", "info", {
      action: "get_wechat_openid_by_userid",
      userId,
      hasOpenid: !!result.openid,
      timestamp: new Date().toISOString(),
    });

    return result;
  } catch (error: unknown) {
    reportError(error as Error, {
      type: "wechat_api_error",
      action: "get_wechat_openid_by_userid",
      userId,
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
}
